<template>
  <div ref="VIREMENT_SALARIE">
    <section-header
        v-if="!hideTitle"
        ref="CI_TRANSACTION_LIST"
        :ressource="ressource"
        :is-extended="extended"
        :dialog="dialog"
        :fiche=fiche
        @extended=setExtended
        @close-dialog=closeSection>
    </section-header>

    <v-row class="py-10 m-0">
      <v-col>
        <v-combobox
            ref="COMBO_BOX"
            v-model=salarieSelected
            class="'edit-field'"
            :items="salarieList"
            :label=labelSalarie
            :placeholder="placeholder"
            :persistent-placeholder="placeholder!=''"
            :outlined="false"
            :ripple="false"
            dense>
        </v-combobox>
      </v-col>
      <v-col>
        <div class="d-flex flex-row-reverse">
          <loader-button
              class="ms-2"
              label="Exporter"
              :loading="exportLoading"
              :disabled="!exportDisabled"
              @callback="exportTransactions">
          </loader-button>
          <loader-button
              class='ms-2'
              type='button'
              :label='generateLabel'
              :loading="generateLoading"
              :disabled='!generateDisabled'
              @callback="generate">
          </loader-button>
          <loader-button
              class="ms-2"
              label="Recherche"
              :loading="searchLoading"
              :disabled="searchDisabled"
              @callback="search">
          </loader-button>
        </div>
      </v-col>
    </v-row>

    <div v-if="extended">
      <v-data-table
          v-model="selected"
          :height="listHeight"
          dense fixed-header
          :headers="headers"
          :items="items"
          :items-per-page="-1"
          :must-sort="true"
          :mobile-breakpoint="100"
          item-key="transactionId"
          class="white"
          :row-props="getRowProps"
          no-data-text="Aucune donnée"
          :disable-pagination="true"
          :hide-default-footer="true"
          :show-select="true"
          return-object
          @toggle-select-all="selectAll">
        <template #[`item.data-table-select`]="props">
          <v-checkbox
              :model-value="isSelected(props.item)"
              :readonly="getDisabled(props.item)"
              :disabled="getDisabled(props.item)"
              @change="(event: any) => handleSelect(event.target.checked, props.item)"
          ></v-checkbox>
        </template>
        <template #[`item.date`]="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template #[`item.datePayed`]="{ item }">
          {{ formatDate(item.datePayed) }}
        </template>
        <template #[`item.montant`]="{ item }">
          {{ formatCurrency(item.montant) }}
        </template>
      </v-data-table>
    </div>

    <v-row class="mt-5">
      <v-col>
        <span class="me-10">Total : {{ totalMontant }} ({{ totalCotis }} paies)</span>
        <span>Sélectionnées : {{ selectMontant }} ({{ selectCotis }} paies)</span>
      </v-col>

    </v-row>
  </div>
</template>
<script lang="ts">
import {RessourceAppSection, AppSections} from '@/ressources/globalRessources';
import {FwkButtons, RessourceFwkButton} from '@/ressources/globalRessources';
import {Sections} from '@/generated/classes/AppSchema';
import Salarie from '@/generated/classes/Salarie';
import ListData, {ListFilter} from '@/generated/classes/ListData';
import Item from '@/framework/base/classes/Item';
import {Extended} from '@/framework/base/classes/Schema';
import {DateFormat} from '@/framework/base/helpers/DateFormat';
import {getCurrency} from '@/framework/base/helpers/Localization';
import TransactionList, {TransactionSalarie, TransactionSalarieQuery} from '@/generated/classes/TransactionList';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import {TransactionStatuts, TransactionTypes} from '@/generated/classes/Transaction';
import {ObjTypes} from '@/generated/classes/ObjTypes';
import {defineComponent} from "vue";

export default defineComponent({
  name: 'SectionTransactionSalarie',
  components: {
    SectionHeader,
    LoaderButton
  },
  props: {
    hideTitle: {
      type: Boolean,
      required: false,
    },
    extended: {
      type: Boolean,
      required: false,
    },
    dialog: {
      type: Boolean,
      required: false,
    },
    fiche: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchLoading: false,
      exportLoading: false,
      generateLoading: false,
      sectionName: Sections.VIREMENT_SALARIE,
      placeholder: 'Sélectionner',
      listHeight: 'calc(100vh - 500px)',
      selectedItems: [] as TransactionSalarie[],
      transactionList: [] as TransactionSalarie[],
      salarieId: new Item(-1, 'A payer'),
      salarieList: [] as Item[],
      labelSalarie: 'Code salarié',
      totalCotis: 0,
      totalMontant: "0,00 €",
      selectCotis: 0,
      selectMontant: "0,00 €",
    };
  },
  computed: {
    ressource(): any {
      return RessourceAppSection(AppSections.VIREMENT_SALARIE);
    },
    generateLabel(): any {
      return RessourceFwkButton(FwkButtons.GENERER_VIREMENT).label;
    },
    headers(): any {
      return [
        {title: "Code", value: 'salarieId', cellClass: '', sortable: true},
        {title: "Nom", value: 'nom', cellClass: '', sortable: true},
        {title: "Prénom", value: 'prenom', cellClass: '', sortable: true},
        {title: "Établissement", value: 'etablissement', cellClass: '', sortable: true},
        {title: "Date", value: 'date', cellClass: '', sortable: true},
        {title: "Montant", value: 'montant', cellClass: '', sortable: true, align: 'right'},
        {title: "N° chèque", value: 'deliveryId', cellClass: '', sortable: true, align: 'right'},
        {title: "Encaissement", value: 'datePayed', cellClass: '', sortable: true, align: 'center'}
      ];
    },
    items(): TransactionSalarie[] {
      return this.transactionList;
    },
    selected: {
      get(): TransactionSalarie[] {
        return this.selectedItems;
      },
      set(transactions: TransactionSalarie[]) {
        const res: TransactionSalarie[] = [];
        this.selectCotis = 0;
        this.selectMontant = "0,00 €"
        let selectMontant = 0;
        transactions.forEach((transaction) => {
          if (!this.getDisabled(transaction)) {
            res.push(transaction);
            this.selectCotis++;
            if (transaction.montant) {
              selectMontant += transaction.montant;
            }
          }
        })
        this.selectMontant = getCurrency(selectMontant);
        this.selectedItems = res;
      },
    },
    searchDisabled(): boolean {
      return false;
    },
    generateDisabled(): boolean {
      return this.selectedItems.length > 0;
    },
    exportDisabled(): boolean {
      return this.selectedItems.length > 0;
    },
    salarieSelected: {
      get(): Item {
        return this.salarieId;
      },
      set(item: Item) {
        this.salarieId = item;
      },
    },
  },
  mounted() {
    Salarie.list(new ListFilter()).then((listData: ListData) => {
      const res: Item[] = [];
      res.push(new Item(-1, 'A payer'));
      const salaries = listData.reccords as Salarie[];
      salaries.forEach((salarie) => {
        if (salarie.clientId > 0 && (salarie.profil & 32) > 0) {
          const label = salarie.clientId.toString() + " - " + salarie.nom + " " + salarie.prenom;
          res.push(new Item(salarie.id, label));
        }
      })
      this.salarieList = res;
    })
    const query = new TransactionSalarieQuery();
    query.salarieId = -1;
    this.loadTable(query);
  },
  methods: {
    loadTable(query: TransactionSalarieQuery): void {
      this.searchLoading = true;
      this.transactionList = [];
      this.selectedItems = [];
      this.totalCotis = 0;
      this.totalMontant = "0,00 €"
      TransactionList.listTransactionsSalarie(query).then((res) => {
        this.transactionList = res;
        this.totalCotis = res.length;
        let totalMontant = 0;
        res.forEach((cotis) => {
          if (cotis.montant) {
            totalMontant += cotis.montant;
          }
        });
        this.totalMontant = getCurrency(totalMontant);
        this.searchLoading = false;
        this.selected(res);
      }).catch(() => {
        this.searchLoading = false;
      });
    },
    handleSelect(isSelected: boolean, item: TransactionSalarie) {
      const selectedItems = this.selectedItems;
      if (isSelected && !selectedItems.includes(item)) {
        selectedItems.push(item);
        this.selected = selectedItems;
      } else {
        this.selected = this.selected.filter(selectedItem => selectedItem.transactionId !== item.transactionId);
      }
    },
    getDisabled(item: TransactionSalarie): boolean {
      return !(item.type == TransactionTypes.VIREMENT_EMIS &&
          item.statut == TransactionStatuts.PROCESSING &&
          item.deliveryId == '' &&
          item.salarieId > 0);
    },
    getRowProps({item}: { item: TransactionSalarie }): Record<string, string> {
      if (item.statut === TransactionStatuts.GENERATION_ERROR) {
        return {class: 'action-bg'};
      }
      return {class: this.getDisabled(item) ? 'p-4 transaction-disabled' : 'p-4'};
    },
    isSelected(item: TransactionSalarie): boolean {
      return !!this.selectedItems.find(a => a.transactionId == item.transactionId);
    },
    selectAll(transactions: TransactionSalarie[]) {
      if (this.selectedItems.length > 0) {
        this.selectedItems = [];
      }
    },

    formatDate(value: Date): string {
      return DateFormat.getDateLabel(value);
    },
    formatCurrency(value: number): string {
      return getCurrency(value);
    },
    search(): void {
      this.selectedItems = [];
      const query = new TransactionSalarieQuery();
      query.salarieId = this.salarieId.key;
      this.loadTable(query);
    },
    generate(): void {
      this.generateLoading = true;
      const ids: number[] = [];
      this.selectedItems.forEach((item) => {
        ids.push(item.transactionId);
      })
      const deliverList = new TransactionList();
      deliverList.objType = ObjTypes.SALARIE;
      deliverList.ids = ids;
      TransactionList.deliverTransactions(deliverList).then(() => {
        this.generateLoading = false;
        this.selectedItems = [];
        this.search();
      })
    },
    exportTransactions(): void {
      // to implement
    },
    setExtended(extended: Extended): void {
      this.$emit('extended', extended);
    },
    closeSection(): void {
      this.$emit('closeDialog');
    },
  },
});
</script>
<style scoped>
:deep(.transaction-disabled > td) {
  color: rgb(var(--v-theme-grey60)) !important;
  background-color: rgb(var(--v-theme-grey97)) !important;
}
</style>
